import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtDateTime } from "../lib/utils-app";
import { ScrollText, LogIn, Eye, Download, Upload, Filter, Users } from "lucide-react";

const PRESETS = [
  ["all", "Tutto", ""],
  ["access", "Accessi", "login,login-google,logout,change-password,reset-password,forgot-password"],
  ["docs", "Documenti", "preview,download,upload,update,delete,ai-parse,bulk-analyze"],
  ["changes", "Modifiche", "create,update,delete,assign-companies,reset-credentials"],
];
const ACTION_LABEL = { login: "Accesso", "login-google": "Accesso Google", logout: "Uscita", preview: "Visualizzato", download: "Scaricato", upload: "Caricato", update: "Modificato", delete: "Eliminato", create: "Creato", "ai-parse": "Lettura AI", "bulk-analyze": "Analisi massiva", "change-password": "Cambio password", "reset-password": "Reset password", "forgot-password": "Richiesta reset", "assign-companies": "Assegnazione aziende", "reset-credentials": "Reset credenziali", trigger: "Avvio job" };
const ACTION_ICON = { login: LogIn, "login-google": LogIn, preview: Eye, download: Download, upload: Upload };
const ENTITY_LABEL = { document: "Documento", user: "Utente", company: "Azienda", worker: "Lavoratore", site: "Cantiere", equipment: "Attrezzatura", share: "Condivisione", studio: "Studio", recipient: "Destinatario", backup: "Backup", settings: "Impostazioni" };

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ preset: "all", user_email: "", date_from: "", date_to: "" });

  useEffect(() => { api.get("/audit/users").then(r => setUsers(r.data)).catch(() => null); }, []);
  useEffect(() => {
    (async () => {
      try {
        const action = PRESETS.find(p => p[0] === f.preset)?.[2] || "";
        const { data } = await api.get("/audit", { params: { action: action || undefined, user_email: f.user_email || undefined, date_from: f.date_from || undefined, date_to: f.date_to || undefined } });
        setLogs(data);
      } catch (e) { setErr(e?.response?.data?.detail || "Non autorizzato"); }
    })();
  }, [f]);

  const sel = "px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm";
  const studios = users.filter(u => u.role === "studio");

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Sicurezza & Conformità</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><ScrollText size={28} /> Registro attività e accessi</h1>
        <p className="text-slate-500 mt-1">Chi ha effettuato l'accesso, quando, e quali documenti sono stati visualizzati, scaricati, caricati o modificati.</p>
      </header>

      {err && <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">{err}</div>}

      {studios.length > 0 && (
        <section data-testid="studio-access-summary">
          <h2 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><Users size={18} /> Ultimo accesso Studi di Consulenza</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {studios.map(u => (
              <button key={u.email} onClick={() => setF({ ...f, user_email: u.email })} data-testid={`studio-access-${u.email}`}
                className={`text-left bg-white border rounded-lg p-3 hover:border-blue-400 ${f.user_email === u.email ? "border-blue-600 ring-1 ring-blue-200" : "border-slate-200"}`}>
                <div className="font-medium text-slate-900">{u.studio_name || u.name}</div>
                <div className="text-xs font-mono text-slate-500">{u.email}</div>
                <div className="text-xs mt-1">{u.last_login ? <span className="text-emerald-700">Ultimo accesso: {fmtDateTime(u.last_login)}</span> : <span className="text-slate-400">Mai acceduto</span>}</div>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="audit-filters">
        <Filter size={16} className="text-slate-400" />
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, preset: k })} data-testid={`audit-preset-${k}`}
              className={`px-3 py-2 text-sm ${f.preset === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <select value={f.user_email} onChange={(e) => setF({ ...f, user_email: e.target.value })} className={sel} data-testid="audit-user">
          <option value="">Tutti gli utenti</option>
          {users.map(u => <option key={u.email} value={u.email}>{u.role === "studio" ? `[Studio] ${u.studio_name || u.name}` : `${u.name || u.email} (${u.role})`}</option>)}
        </select>
        <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={sel} data-testid="audit-from" />
        <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={sel} data-testid="audit-to" />
        {(f.user_email || f.date_from || f.date_to || f.preset !== "all") && <button onClick={() => setF({ preset: "all", user_email: "", date_from: "", date_to: "" })} className="text-sm text-blue-700 hover:underline">Azzera filtri</button>}
        <span className="text-sm text-slate-500 ml-auto" data-testid="audit-count">{logs.length} eventi</span>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Data/ora</th>
              <th className="text-left px-4 py-3 font-medium">Utente</th>
              <th className="text-left px-4 py-3 font-medium">Azione</th>
              <th className="text-left px-4 py-3 font-medium">Oggetto</th>
              <th className="text-left px-4 py-3 font-medium">Dettagli</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Nessuna attività registrata con questi filtri.</td></tr>}
            {logs.map(l => {
              const Icon = ACTION_ICON[l.action];
              const u = users.find(x => x.email === l.user_email);
              return (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid="audit-row">
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(l.timestamp)}</td>
                  <td className="px-4 py-3">
                    <div>{l.user_email}</div>
                    {u?.role === "studio" && <div className="text-[11px] text-blue-700">Studio: {u.studio_name}</div>}
                  </td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1">{Icon && <Icon size={14} className="text-slate-500" />}{ACTION_LABEL[l.action] || l.action}</span></td>
                  <td className="px-4 py-3">
                    <div>{ENTITY_LABEL[l.entity_type] || l.entity_type}</div>
                    {l.document_name && <div className="text-xs text-slate-500 truncate max-w-xs">{l.document_name}{l.company_name && ` · ${l.company_name}`}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-xs">{l.details}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
